import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { exploreSampleAndGo } from "@/lib/sampleData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Lock, FileQuestion, HelpCircle, Clock, Laptop, AlertTriangle,
  BarChart3, Headset, Pencil, CheckCircle2, Sparkles,
} from "lucide-react";

const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const OPTIONS = [
  { code: "privacy", Icon: Lock, label: "I'm not comfortable uploading real customer data yet" },
  { code: "no_file", Icon: FileQuestion, label: "I don't have the Excel file with me" },
  { code: "not_sure", Icon: HelpCircle, label: "I'm not sure what file to upload" },
  { code: "no_time", Icon: Clock, label: "I don't have time right now" },
  { code: "later_laptop", Icon: Laptop, label: "I'll do this later from my laptop" },
  { code: "upload_error", Icon: AlertTriangle, label: "I faced an error while uploading" },
  { code: "understand_first", Icon: BarChart3, label: "I want to understand the product better first" },
  { code: "need_help", Icon: Headset, label: "I'd like someone to help me with the first upload" },
  { code: "other", Icon: Pencil, label: "Other (please specify)" },
];

// Priority order for choosing the primary reason to respond to.
const PRIORITY = ["upload_error", "privacy", "need_help", "not_sure", "understand_first", "no_file", "no_time", "later_laptop", "other"];

const RESPONSES = {
  upload_error: { text: "Sorry about that — our team will reach out to help. You can also try the upload again now.", cta: "Try again", action: "import" },
  privacy: { text: "Your data is encrypted, stays in your private workspace, and you can delete it anytime. Prefer to see it in action first? Explore a sample portfolio.", cta: "Explore a sample portfolio", action: "sample" },
  need_help: { text: "Happy to help — we'll arrange a guided onboarding session to load your first file with you.", cta: null, action: null },
  not_sure: { text: "No problem — download our sample template on the import screen and match your columns. It takes two minutes.", cta: "Go to import", action: "import" },
  understand_first: { text: "Makes sense — explore a fully-loaded sample portfolio to see exactly what you'll get before importing your own.", cta: "Explore a sample portfolio", action: "sample" },
  no_file: { text: "No problem — we'll send you a quick link so you can upload the moment you have the file.", cta: null, action: null },
  no_time: { text: "Totally understandable — we'll email you a link to pick up right where you left off.", cta: null, action: null },
  later_laptop: { text: "Great — we'll email you a link so you can finish the import from your laptop.", cta: null, action: null },
  other: { text: "Thank you — your feedback helps us make the first upload easier.", cta: null, action: null },
};

const APP_ROUTES = ["/dashboard", "/import"];

export const ExitSurvey = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [other, setOther] = useState("");
  const [done, setDone] = useState(false);
  const shown = useRef(false);

  const uid = user?.user_id || user?.email || "";
  const eligibleRole = ["client_admin", "manager"].includes(user?.role);
  const onAppScreen = APP_ROUTES.includes(pathname);

  // Preview/QA hook: ?survey=preview opens the survey immediately (bypasses gating). Harmless in prod.
  useEffect(() => {
    if (!open && new URLSearchParams(window.location.search).get("survey") === "preview") {
      shown.current = true;
      setOpen(true);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || !eligibleRole || !onAppScreen || shown.current) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)")) { /* still ok */ }
    try {
      if (localStorage.getItem(`ros_exit_survey_${uid}`)) return; // already answered/dismissed
      if (localStorage.getItem(`recoveros_excel_uploaded_${uid}`)) return; // already uploaded
    } catch (e) { /* ignore */ }

    let dwell, armExit, cleaned = false;
    const trigger = () => {
      if (shown.current || cleaned) return;
      shown.current = true;
      logEvent("exit_survey_shown", {});
      setOpen(true);
      cleanup();
    };
    const onMouseOut = (e) => { if (e.clientY <= 0 && !e.relatedTarget) trigger(); };
    const cleanup = () => {
      cleaned = true;
      clearTimeout(dwell); clearTimeout(armExit);
      document.removeEventListener("mouseout", onMouseOut);
    };

    // Confirm the user genuinely has no accounts yet before arming (accurate, avoids false positives).
    api.get("/import/status").then((r) => {
      if ((r.data?.total_accounts ?? 0) > 0) {
        try { localStorage.setItem(`recoveros_excel_uploaded_${uid}`, "1"); } catch (e) { /* ignore */ }
        return;
      }
      dwell = setTimeout(trigger, 90000); // ≥90s dwell — also the mobile fallback (no exit-intent on touch)
      armExit = setTimeout(() => document.addEventListener("mouseout", onMouseOut), 20000); // desktop exit-intent, not immediate
    }).catch(() => {});

    return cleanup;
  }, [user, eligibleRole, onAppScreen, uid]);

  const toggle = (code) => setSelected((s) => s.includes(code) ? s.filter((c) => c !== code) : [...s, code]);

  const submit = async () => {
    const reasons = selected.length ? selected : [];
    try {
      await api.post("/activation-survey", { reasons, other_text: other });
    } catch (e) { logEvent("activation_survey_submit_failed", {}); }
    logEvent("exit_survey_submitted", { reasons });
    try { localStorage.setItem(`ros_exit_survey_${uid}`, "1"); } catch (e) { /* ignore */ }
    setDone(true);
  };

  const dismiss = () => {
    if (!done) {
      logEvent("exit_survey_dismissed", {});
      try {
        // Show at most twice: a 2nd dismissal (or any submit) permanently silences it.
        const k = `ros_exit_survey_dismiss_${uid}`;
        const n = (parseInt(localStorage.getItem(k) || "0", 10) || 0) + 1;
        localStorage.setItem(k, String(n));
        if (n >= 2) localStorage.setItem(`ros_exit_survey_${uid}`, "1");
      } catch (e) { /* ignore */ }
    }
    setOpen(false);
  };

  const primary = PRIORITY.find((c) => selected.includes(c)) || "other";
  const resp = RESPONSES[primary] || RESPONSES.other;

  const runAction = (action) => {
    setOpen(false);
    if (action === "sample") exploreSampleAndGo();
    else if (action === "import") navigate("/import");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="sm:max-w-md rounded-none border-2 border-slate-200" data-testid="exit-survey">
        {done ? (
          <div className="py-4 text-center" data-testid="exit-survey-thanks">
            <CheckCircle2 className="mx-auto h-11 w-11 text-[#16A34A]" />
            <h3 className="mt-3 text-lg font-extrabold text-slate-900" style={JK}>Thanks for the honesty</h3>
            <p className="mt-2 text-sm text-slate-600">{resp.text}</p>
            <div className="mt-5 flex flex-col gap-2">
              {resp.cta && (
                <Button onClick={() => runAction(resp.action)} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="exit-survey-cta">
                  {resp.action === "sample" ? <Sparkles className="h-4 w-4" /> : null}{resp.cta}
                </Button>
              )}
              <button onClick={() => setOpen(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="exit-survey-close">Close</button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold text-slate-900" style={JK}>What's stopping you from uploading your Excel portfolio today?</DialogTitle>
              <DialogDescription className="text-slate-600">Pick all that apply — it takes 5 seconds and genuinely shapes what we build next.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 py-1 max-h-[46vh] overflow-y-auto">
              {OPTIONS.map((o) => {
                const active = selected.includes(o.code);
                return (
                  <label key={o.code}
                    className={`flex items-center gap-3 rounded-sm border px-3 py-2.5 cursor-pointer transition-colors ${active ? "border-[#0B3D91] bg-[#0B3D91]/[0.05]" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`exit-survey-opt-${o.code}`}>
                    <input type="checkbox" checked={active} onChange={() => toggle(o.code)} className="h-4 w-4 accent-[#0B3D91]" />
                    <o.Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#0B3D91]" : "text-slate-400"}`} />
                    <span className="text-sm text-slate-700">{o.label}</span>
                  </label>
                );
              })}
              {selected.includes("other") && (
                <textarea value={other} onChange={(e) => setOther(e.target.value)} rows={2} placeholder="Tell us more…"
                  className="w-full rounded-sm border border-slate-200 px-3 py-2 text-sm mt-1" data-testid="exit-survey-other-text" />
              )}
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <button onClick={dismiss} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="exit-survey-skip">Not now</button>
              <Button onClick={submit} disabled={selected.length === 0} className="rounded-sm bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="exit-survey-submit">Send feedback</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
